/**
 * cart-loyalty.js — Redeem-your-points widget for the cart page AND the
 * cart drawer. Idempotent per-widget, and re-scans on cart updates so the
 * widget is re-initialised after the drawer's footer is re-rendered by
 * AJAX cart merges.
 *
 * Two programs, decided by the Liquid that renders the widget
 * (data-program="pro" | "patio"), never both for one customer:
 *   - pro:   Pro Circle. Balance from the admin API by email (read only);
 *            redeem through the App Proxy (/apps/petra/pro/redeem), which
 *            identifies the customer by their Shopify session.
 *   - patio: El Patio. Everything through the App Proxy (/apps/petra/me,
 *            /apps/petra/redeem); no email leaves the browser.
 *
 * Two-mode picker:
 *   - "Select Discount" (Mode A): pick a step → POST redeem → no redirect,
 *     swap to Mode B with the new code added.
 *   - "Select a Reward" (Mode B): pick an existing unused code → redirect
 *     to /discount/CODE?redirect=/checkout so the customer sees it applied.
 *
 * Default mode on load:
 *   - Mode B if the customer has 1+ unused active codes (available_codes).
 *   - Otherwise Mode A.
 */
(function () {
  const PROXY = '/apps/petra';
  // Pro Circle steps, mirror of REDEMPTION_TIERS in src/routes/loyalty.js.
  const PRO_STEPS = [
    { pts: 200,  dollars: 10, min_order: 0 },
    { pts: 400,  dollars: 20, min_order: 75 },
    { pts: 800,  dollars: 40, min_order: 100 },
    { pts: 1600, dollars: 80, min_order: 250 }
  ];

  const STR = {
    en: { have: (p) => `You have ${p} points! Redeem for discounts on this order.`, first: 'Place your first order to start earning points.',
          earnMore: (p, n) => `You have ${p} points. Earn ${n} more to unlock your first reward.`, making: 'Generating your discount code…',
          fail: 'Could not redeem. Please try again.', connect: 'Could not connect. Please try again.', load: 'Could not load your points. Please refresh.',
          created: (d) => `New $${d} reward created — apply it below.`, pick: (p) => `You have ${p} points! Pick a reward to apply.`,
          applying: (c) => `Applying reward (${c})…`, off: (d, p) => `$${d} Off (${p} points)`, min: (m) => ` · orders $${m}+`, exp: (d) => ` — expires ${d}` },
    es: { have: (p) => `Tienes ${p} puntos. Canjéalos por descuento en esta orden.`, first: 'Tu primera orden empieza a sumar puntos.',
          earnMore: (p, n) => `Tienes ${p} puntos. Te faltan ${n} para tu primera recompensa.`, making: 'Creando tu código...',
          fail: 'No se pudo canjear. Inténtalo otra vez.', connect: 'No hubo conexión. Inténtalo otra vez.', load: 'No pudimos cargar tus puntos. Recarga la página.',
          created: (d) => `Recompensa de $${d} creada. Pónla abajo.`, pick: (p) => `Tienes ${p} puntos. Escoge una recompensa.`,
          applying: (c) => `Poniendo la recompensa (${c})...`, off: (d, p) => `$${d} de descuento (${p} puntos)`, min: (m) => ` · órdenes de $${m}+`, exp: (d) => ` — vence ${d}` }
  };

  function formatDollars(n) {
    return Number.isInteger(n) ? n.toFixed(0) : Number(n).toFixed(2);
  }

  function formatExpiry(iso, locale) {
    if (!iso) return '';
    const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(locale === 'es' ? 'es-PR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  async function initWidget(root) {
    if (!root || root.dataset.clInitialized === '1') return;
    root.dataset.clInitialized = '1';

    const program = root.dataset.program === 'pro' ? 'pro' : 'patio';
    const locale = root.dataset.locale === 'es' ? 'es' : 'en';
    const t = STR[locale];
    const API_BASE = (root.dataset.apiBase || '').replace(/\/$/, '');
    const email = root.dataset.customerEmail || '';
    if (program === 'pro' && (!email || !API_BASE || API_BASE.includes('your-app.onrender.com'))) return;

    const sub = root.querySelector('[data-cl-sub]');
    const discountWrap = root.querySelector('[data-cl-select-wrap]');
    const select = root.querySelector('[data-cl-select]');
    const rewardsWrap = root.querySelector('[data-cl-rewards-wrap]');
    const rewardsSelect = root.querySelector('[data-cl-rewards]');
    const showRewardsLink = root.querySelector('[data-cl-show-rewards]');
    const showDiscountLink = root.querySelector('[data-cl-show-discount]');
    const msg = root.querySelector('[data-cl-msg]');

    let availableCodes = [];
    let steps = program === 'pro' ? PRO_STEPS : [];
    let affordable = [];

    function rewardOptionLabel(c) {
      const dollars = c.dollars != null ? `$${formatDollars(c.dollars)}` : '';
      const exp = c.expires_at ? t.exp(formatExpiry(c.expires_at, locale)) : '';
      return `${dollars} — ${c.code}${c.min_order ? t.min(c.min_order) : ''}${exp}`;
    }

    function showOnly(which) {
      if (discountWrap) discountWrap.hidden = which !== 'discount';
      if (rewardsWrap) rewardsWrap.hidden = which !== 'rewards';
    }

    function renderDiscountMode() {
      if (!select) return;
      [...select.querySelectorAll('option:not([value=""])')].forEach(o => o.remove());
      affordable.forEach(s => {
        const opt = document.createElement('option');
        opt.value = String(s.pts);
        opt.textContent = t.off(formatDollars(s.dollars), s.pts) + (s.min_order ? t.min(s.min_order) : '');
        select.appendChild(opt);
      });
      select.value = '';
      select.disabled = false;
      if (showRewardsLink) showRewardsLink.hidden = availableCodes.length === 0;
      showOnly('discount');
    }

    function renderRewardsMode() {
      if (!rewardsSelect) return;
      [...rewardsSelect.querySelectorAll('option:not([value=""])')].forEach(o => o.remove());
      availableCodes.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.code;
        opt.textContent = rewardOptionLabel(c);
        rewardsSelect.appendChild(opt);
      });
      rewardsSelect.value = '';
      rewardsSelect.disabled = false;
      if (showDiscountLink) showDiscountLink.hidden = affordable.length === 0;
      showOnly('rewards');
    }

    async function postRedeem(pts) {
      const url = program === 'pro' ? `${PROXY}/pro/redeem` : `${PROXY}/redeem?locale=${locale}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ points: pts })
      });
      let data = {};
      try { data = await res.json(); } catch (e) { data = {}; }
      return { ok: res.ok && data.success, data };
    }

    async function handleRedeem() {
      const pts = Number(select.value);
      if (!pts) return;
      select.disabled = true;
      if (msg) msg.textContent = t.making;
      try {
        const { ok, data } = await postRedeem(pts);
        if (!ok) {
          if (msg) msg.textContent = data.error || t.fail;
          select.disabled = false;
          select.value = '';
          return;
        }
        const step = steps.find(s => s.pts === pts);
        const dollars = step ? step.dollars : Number(data.discount_amount || data.dollars);
        availableCodes.unshift({ code: data.code, dollars, min_order: step ? step.min_order : 0, expires_at: data.expires_at });
        const newPoints = Number(data.new_total) || 0;
        affordable = affordable.filter(s => s.pts <= newPoints);
        if (sub) sub.textContent = t.pick(newPoints);
        if (msg) msg.textContent = t.created(formatDollars(dollars));
        renderRewardsMode();
      } catch (err) {
        if (msg) msg.textContent = t.connect;
        select.disabled = false;
        select.value = '';
      }
    }

    function handleRewardSelect() {
      const code = rewardsSelect.value;
      if (!code) return;
      rewardsSelect.disabled = true;
      if (msg) msg.textContent = t.applying(code);
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
    }

    try {
      let points = 0;
      let member = null;
      if (program === 'pro') {
        const res = await fetch(`${API_BASE}/api/loyalty-public/${encodeURIComponent(email)}`);
        const data = await res.json();
        member = data.member;
        if (member) {
          points = Number(member.loyalty_points) || 0;
          const cfg = data.config || {};
          const minPts = Number(cfg.min_redemption_pts) || 100;
          affordable = PRO_STEPS.filter(s => s.pts <= points && s.pts >= minPts);
        }
      } else {
        const res = await fetch(`${PROXY}/me?locale=${locale}`, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        member = data.member;
        steps = (data.config && data.config.redemption_steps) || [];
        if (member) {
          points = Number(member.points) || 0;
          affordable = steps.filter(s => s.pts <= points);
        }
      }

      if (!member) {
        if (sub) sub.textContent = t.first;
        return;
      }
      availableCodes = Array.isArray(member.available_codes) ? member.available_codes.slice() : [];
      if (sub) sub.textContent = t.have(points);

      if (select) select.addEventListener('change', handleRedeem);
      if (rewardsSelect) rewardsSelect.addEventListener('change', handleRewardSelect);
      if (showRewardsLink) showRewardsLink.addEventListener('click', e => { e.preventDefault(); renderRewardsMode(); });
      if (showDiscountLink) showDiscountLink.addEventListener('click', e => { e.preventDefault(); renderDiscountMode(); });

      if (availableCodes.length > 0) {
        renderRewardsMode();
      } else if (affordable.length > 0) {
        renderDiscountMode();
      } else if (sub && steps.length) {
        sub.textContent = t.earnMore(points, steps[0].pts - points);
      }
    } catch (err) {
      if (sub) sub.textContent = t.load;
    }
  }

  function scan() {
    document.querySelectorAll('[data-cart-loyalty]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('on:cart:after-merge', scan);
  document.addEventListener('dispatch:cart-drawer:refresh', scan);
  document.addEventListener('cart:refresh', scan);

  const mo = new MutationObserver(() => {
    document.querySelectorAll('[data-cart-loyalty]:not([data-cl-initialized="1"])').forEach(initWidget);
  });
  if (document.body) {
    mo.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => mo.observe(document.body, { childList: true, subtree: true }));
  }
})();
